import type { CompanyResponse } from '@shared/schemas';

// ── Types ──

export const STALE_DAYS = 30;

export interface EnrichmentProfileOption {
  id: string;
  full_name: string;
  job_title: string | null;
  linkedin_id: string | null;
  data_updated_at: string;
  days_since_update: number;
  relativeTime: string;
  isStale: boolean;
  /** true when the entity has never been enriched (no linkedin_id) */
  isNew: boolean;
}

/**
 * Generic enrichment-option builder. Maps any entity into EnrichmentProfileOption
 * using the provided field accessors, with new/stale/fresh segmentation logic.
 */
export function toEnrichmentOptionsGeneric<T>(
  items: T[],
  accessors: {
    getId: (item: T) => string;
    getName: (item: T) => string;
    getSubtitle: (item: T) => string | null;
    getLinkedinId: (item: T) => string | null;
    getDataUpdatedAt: (item: T) => string | null;
  },
): EnrichmentProfileOption[] {
  const now = Date.now();
  return items
    .map((item) => {
      const id = accessors.getId(item);
      const full_name = accessors.getName(item);
      const job_title = accessors.getSubtitle(item);
      const linkedin_id = accessors.getLinkedinId(item);
      const data_updated_at = accessors.getDataUpdatedAt(item);

      // No linkedin_id → never enriched → "New"
      if (!linkedin_id) {
        return {
          id,
          full_name,
          job_title,
          linkedin_id: null,
          data_updated_at: '',
          days_since_update: Infinity,
          relativeTime: 'Never',
          isStale: true,
          isNew: true,
        };
      }

      // Has linkedin_id but no date; treat as stale update
      if (!data_updated_at) {
        return {
          id,
          full_name,
          job_title,
          linkedin_id,
          data_updated_at: '',
          days_since_update: Infinity,
          relativeTime: 'Never',
          isStale: true,
          isNew: false,
        };
      }

      const updatedMs = new Date(data_updated_at).getTime();
      const diffMs = now - updatedMs;
      const totalMinutes = Math.floor(diffMs / 60_000);
      const days = Math.floor(totalMinutes / 1440);
      const hours = Math.floor((totalMinutes % 1440) / 60);
      const minutes = totalMinutes % 60;

      let relativeTime: string;
      if (days > 0) {
        relativeTime = `${days}d ${String(hours).padStart(2, '0')}h${String(minutes).padStart(2, '0')}`;
      } else if (hours > 0) {
        relativeTime = `${hours}h${String(minutes).padStart(2, '0')}`;
      } else {
        relativeTime = `${minutes}m`;
      }

      return {
        id,
        full_name,
        job_title,
        linkedin_id,
        data_updated_at,
        days_since_update: days,
        relativeTime,
        isStale: days > STALE_DAYS,
        isNew: false,
      };
    })
    .sort((a, b) => b.days_since_update - a.days_since_update);
}

/**
 * Converts raw profile data into enrichment options with new/stale/fresh segmentation.
 */
export function toEnrichmentOptions(
  profiles: {
    id: string;
    full_name: string;
    job_title: string | null;
    linkedin_id: string | null;
    data_updated_at: string | null;
  }[],
): EnrichmentProfileOption[] {
  return toEnrichmentOptionsGeneric(profiles, {
    getId: (p) => p.id,
    getName: (p) => p.full_name,
    getSubtitle: (p) => p.job_title,
    getLinkedinId: (p) => p.linkedin_id,
    getDataUpdatedAt: (p) => p.data_updated_at,
  });
}

/**
 * Converts company data into enrichment options compatible with
 * the same new/stale/fresh segmentation as profiles.
 */
export function toEnrichmentCompanyOptions(
  companies: CompanyResponse[],
): EnrichmentProfileOption[] {
  return toEnrichmentOptionsGeneric(companies, {
    getId: (c) => c.id,
    getName: (c) => c.name,
    getSubtitle: (c) => c.industry ?? null,
    getLinkedinId: (c) => c.linkedin_id ?? null,
    getDataUpdatedAt: (c) => c.data_updated_at ?? null,
  });
}
